import type { EnderecoPayload } from "@/lib/services/assinatura-service"
import { escreverFaltasDoEndereco, faltasDoEndereco } from "@/lib/utils/endereco"

/**
 * A conta do cartão de crédito, sem tela: a bandeira, a máscara do número, a
 * conferência dos dígitos (Luhn), a validade e o código de segurança.
 *
 * Existe desde o split no Pagar.me (21/10/2026), quando o cartão passou a ser
 * digitado NA NOSSA página e transformado em token no navegador, direto no
 * Pagar.me (`lib/services/pagarme-service.ts`). Antes ele era digitado na página
 * do banco, e conferir era problema de lá.
 *
 * Mora fora dos componentes porque duas famílias de peça desenham o mesmo cartão:
 * o [campo do cartão](../../components/design-new/campo-cartao.tsx) do sistema (as
 * páginas de pagar, sempre claras) e o `CartaoDaPagina` da página de vendas do
 * personal, com a cor e a fonte dele. A regra é uma só; a roupa é que muda.
 *
 * NADA AQUI GUARDA, ENVIA OU ESCREVE O NÚMERO EM LUGAR NENHUM: as funções recebem
 * o texto digitado e devolvem a resposta. Não ponha `console.log` com o cartão.
 */

export type Bandeira = "visa" | "mastercard" | "elo" | "amex" | "hipercard"

/** O nome escrito de cada bandeira, por extenso: "AmEx" é sigla de quem já sabe. */
export const NOME_DA_BANDEIRA: Record<Bandeira, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  elo: "Elo",
  amex: "American Express",
  hipercard: "Hipercard",
}

type Faixa = number | readonly [number, number]

/**
 * Os começos (os seis primeiros dígitos) da Elo. A Elo é brasileira e vive em
 * faixas espalhadas, várias delas começando com 4 e com 5: é por isso que ela é
 * conferida ANTES da Visa e da Mastercard, e por isso a Visa só é dita depois do
 * sexto dígito. É a lista que as bibliotecas de checkout usam.
 */
const ELO: readonly Faixa[] = [
  401178, 401179, 431274, 438935, 451416, 457393, 457631, 457632, 504175,
  [506699, 506778], [509000, 509999], 627780, 636297, 636368,
  [650031, 650033], [650035, 650051], [650405, 650439], [650485, 650538],
  [650541, 650598], [650700, 650718], [650720, 650727], [650901, 650978],
  [651652, 651679], [655000, 655019], [655021, 655058],
]

/** Os começos da Hipercard: o 606282 e os de dezenove dígitos, que começam com 3841. */
const HIPERCARD: readonly Faixa[] = [606282, 384100, 384140, 384160]

/** Quantos dígitos cada bandeira tem. Sem bandeira conhecida, de 13 a 19. */
const TAMANHOS: Record<Bandeira, number[]> = {
  visa: [13, 16, 19],
  mastercard: [16],
  elo: [16],
  amex: [15],
  hipercard: [16, 19],
}
const TAMANHOS_SEM_BANDEIRA = [13, 14, 15, 16, 17, 18, 19]

export function soDigitos(valor: string): string {
  return valor.replace(/\D/g, "")
}

function naFaixa(bin: number, faixas: readonly Faixa[]): boolean {
  return faixas.some((f) => (typeof f === "number" ? bin === f : bin >= f[0] && bin <= f[1]))
}

/**
 * A bandeira pelo começo do número, ou `null` enquanto não dá para ter certeza.
 *
 * Amex (34, 37) e Mastercard (51 a 55, e 2221 a 2720) se sabem cedo. O resto
 * espera o sexto dígito, porque a Elo divide começos com a Visa (o 4011 78 é
 * Elo): dizer "Visa" no primeiro dígito e trocar para "Elo" no sexto faria a
 * pessoa achar que digitou errado.
 */
export function bandeiraDoCartao(numero: string): Bandeira | null {
  const d = soDigitos(numero)
  if (/^3[47]/.test(d)) return "amex"
  if (/^5[1-5]/.test(d)) return "mastercard"
  if (d.length >= 4) {
    const quatro = Number(d.slice(0, 4))
    if (quatro >= 2221 && quatro <= 2720) return "mastercard"
  }
  if (d.length < 6) return null
  const bin = Number(d.slice(0, 6))
  if (naFaixa(bin, ELO)) return "elo"
  if (naFaixa(bin, HIPERCARD)) return "hipercard"
  if (d.startsWith("4")) return "visa"
  return null
}

function tamanhosDe(bandeira: Bandeira | null): number[] {
  return bandeira ? TAMANHOS[bandeira] : TAMANHOS_SEM_BANDEIRA
}

/**
 * O número como está no cartão: de quatro em quatro, e 4-6-5 no American Express.
 * A máscara é o limite: depois do último dígito que a bandeira tem, não entra mais.
 */
export function mascaraNumeroDoCartao(valor: string): string {
  const bandeira = bandeiraDoCartao(valor)
  const d = soDigitos(valor).slice(0, Math.max(...tamanhosDe(bandeira)))
  if (bandeira === "amex") {
    return [d.slice(0, 4), d.slice(4, 10), d.slice(10, 15)].filter(Boolean).join(" ")
  }
  return d.replace(/(\d{4})(?=\d)/g, "$1 ")
}

/**
 * A conta do último dígito (Luhn), a mesma que o banco faz.
 *
 * Pega o dígito trocado e o número pela metade ANTES de ir ao Pagar.me: sem ela,
 * o erro chegaria depois de uma ida e volta de rede, com uma mensagem genérica.
 */
export function luhnValido(numero: string): boolean {
  const d = soDigitos(numero)
  if (d.length < 12) return false
  let soma = 0
  let dobrar = false
  for (let i = d.length - 1; i >= 0; i--) {
    let n = d.charCodeAt(i) - 48
    if (dobrar) {
      n *= 2
      if (n > 9) n -= 9
    }
    soma += n
    dobrar = !dobrar
  }
  return soma % 10 === 0
}

/**
 * A validade como está no cartão: "MM/AA".
 *
 * A barra entra sozinha no terceiro dígito, e não no segundo: barra que nasce
 * junto com o mês prende o apagar ("08/" apaga a barra e volta para "08/"). Um
 * primeiro dígito de 2 a 9 ganha o zero na frente, porque nenhum mês começa
 * assim: quem digita "8" quer agosto.
 */
export function mascaraValidade(valor: string): string {
  let d = soDigitos(valor).slice(0, 4)
  if (d.length === 1 && Number(d) > 1) d = `0${d}`
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d
}

/** Mês e ano (com quatro dígitos) da validade, ou `null` se ela está incompleta. */
export function lerValidade(valor: string): { mes: number; ano: number } | null {
  const d = soDigitos(valor)
  if (d.length !== 4) return null
  return { mes: Number(d.slice(0, 2)), ano: 2000 + Number(d.slice(2)) }
}

/** O código de segurança tem 4 números no American Express e 3 no resto. */
export function tamanhoDoCodigo(bandeira: Bandeira | null): 3 | 4 {
  return bandeira === "amex" ? 4 : 3
}

/** Onde fica o código, escrito: no American Express ele é impresso na frente. */
export function ondeFicaOCodigo(bandeira: Bandeira | null): string {
  return bandeira === "amex"
    ? "No American Express, são os 4 números na frente do cartão."
    : "Os 3 números atrás do cartão."
}

/** O código só com os números, e nunca mais que a bandeira aceita. */
export function mascaraCodigo(valor: string, bandeira: Bandeira | null): string {
  return soDigitos(valor).slice(0, tamanhoDoCodigo(bandeira))
}

/** O nome como o banco aceita: sem acento, sem espaço sobrando, até 64 letras. */
export function nomeParaOCartao(nome: string): string {
  return nome
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 64)
}

// ── O que falta em cada campo ──────────────────────────────────────────────

export type CampoDoCartao = "numero" | "nome" | "validade" | "codigo"

/** O cartão como a tela guarda: o texto de cada campo, já com a máscara. */
export type CartaoDigitado = Record<CampoDoCartao, string>

export const CARTAO_VAZIO: CartaoDigitado = { numero: "", nome: "", validade: "", codigo: "" }

/** A ordem dos campos na tela, que é a ordem em que a falta é dita. */
export const ORDEM_DO_CARTAO: CampoDoCartao[] = ["numero", "nome", "validade", "codigo"]

/*
  As frases dizem O QUE FAZER, como as do CPF ("Confira: parece que falta
  número"), e nunca "cartão inválido": soa como acusação e não ajuda quem digitou
  um dígito trocado.
*/

export function erroDoNumero(numero: string): string | null {
  const d = soDigitos(numero)
  if (!d) return "Escreva o número do cartão."
  const tamanhos = tamanhosDe(bandeiraDoCartao(d))
  if (!tamanhos.includes(d.length)) return "Confira: parece que falta número."
  if (!luhnValido(d)) return "Confira os números: algum está trocado."
  return null
}

export function erroDoNome(nome: string): string | null {
  const t = nome.trim()
  if (t.length < 2) return "Escreva o nome como está no cartão."
  if (/\d/.test(t)) return "O nome do cartão não leva números."
  return null
}

export function erroDaValidade(validade: string, hoje: Date = new Date()): string | null {
  if (!soDigitos(validade)) return "Escreva a validade do cartão."
  const v = lerValidade(validade)
  if (!v) return "Confira: a validade tem mês e ano, como 08/29."
  if (v.mes < 1 || v.mes > 12) return "Confira o mês: ele vai de 01 a 12."
  const mesDeHoje = hoje.getFullYear() * 12 + hoje.getMonth()
  const mesDoCartao = v.ano * 12 + (v.mes - 1)
  if (mesDoCartao < mesDeHoje) return "Este cartão já venceu. Use outro cartão."
  if (v.ano > hoje.getFullYear() + 20) return "Confira o ano da validade."
  return null
}

export function erroDoCodigo(codigo: string, bandeira: Bandeira | null): string | null {
  const d = soDigitos(codigo)
  if (!d) return "Escreva o código de segurança."
  const tamanho = tamanhoDoCodigo(bandeira)
  if (d.length !== tamanho) {
    return tamanho === 4 ? "No American Express, o código tem 4 números." : "O código tem 3 números."
  }
  return null
}

/** O erro de cada campo, ou `null` onde está tudo certo. */
export function errosDoCartao(
  cartao: CartaoDigitado,
  hoje: Date = new Date(),
): Record<CampoDoCartao, string | null> {
  const bandeira = bandeiraDoCartao(cartao.numero)
  return {
    numero: erroDoNumero(cartao.numero),
    nome: erroDoNome(cartao.nome),
    validade: erroDaValidade(cartao.validade, hoje),
    codigo: erroDoCodigo(cartao.codigo, bandeira),
  }
}

/** O primeiro problema do cartão, na ordem da tela, ou `null` quando ele está pronto. */
export function primeiroErroDoCartao(cartao: CartaoDigitado, hoje: Date = new Date()): string | null {
  const erros = errosDoCartao(cartao, hoje)
  for (const campo of ORDEM_DO_CARTAO) {
    if (erros[campo]) return erros[campo]
  }
  return null
}

/**
 * Por que o botão de pagar no cartão está apagado, numa frase só: o primeiro
 * problema do cartão e, com ele pronto, o que falta no endereço da fatura.
 *
 * Uma frase por vez, como no resto das páginas de pagar: listar tudo de uma vez faz
 * quem esqueceu um campo ler um parágrafo para descobrir qual.
 */
export function impedimentoDoCartao(
  cartao: CartaoDigitado,
  endereco: EnderecoPayload,
  hoje: Date = new Date(),
): string | null {
  const doCartao = primeiroErroDoCartao(cartao, hoje)
  if (doCartao) return doCartao
  const faltas = faltasDoEndereco(endereco)
  const escritas = escreverFaltasDoEndereco(faltas)
  if (!escritas) return null
  return `${faltas.length === 1 ? "Falta" : "Faltam"} ${escritas} no endereço da fatura.`
}

// ── O endereço da fatura, como a API recebe ────────────────────────────────

/** O endereço de cobrança que vai junto do token: o `line_1` do Pagar.me é "número, rua, bairro". */
export type EnderecoDaFatura = { linha: string; cep: string; cidade: string; uf: string }

/**
 * O endereço digitado no formato que a API repassa ao Pagar.me.
 *
 * O complemento não entra: o pedido do split leva só a linha 1, e o antifraude
 * confere rua, número, bairro, CEP e cidade.
 */
export function enderecoDaFatura(e: EnderecoPayload): EnderecoDaFatura {
  return {
    linha: [e.numero, e.logradouro, e.bairro]
      .map((parte) => (parte ?? "").trim())
      .filter(Boolean)
      .join(", ")
      .slice(0, 250),
    cep: soDigitos(e.cep).slice(0, 8),
    cidade: e.cidade.trim().slice(0, 80),
    uf: e.estado.trim().toUpperCase().slice(0, 2),
  }
}
